import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { DOCK_DRAG_HINT_ID } from '@/components/FloatingActionBar/floatingActionBarPosition';
import { cn } from '@/lib/utils';

export interface FloatingActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    /** Accessible name for the control. The graphic inside is always presentational. */
    label: string;
    children: ReactNode;
}

// Each button carries the bar's drag hint as its description, since a description is announced
// only from the focused element. On the pill it would be present but undiscoverable.
export const FloatingActionButton = forwardRef<HTMLButtonElement, FloatingActionButtonProps>(
    ({ label, children, className, ...props }, ref) => (
        <button
            ref={ref}
            type="button"
            aria-label={label}
            aria-describedby={DOCK_DRAG_HINT_ID}
            className={cn(
                'size-[46px] lg:size-[62px] shrink-0 rounded-full',
                'flex items-center justify-center p-1.5 lg:p-2',
                // The icon assets are bare glyphs so the circular backing comes from here.
                'light:bg-gz-surface-medium dark:bg-gz-marine-500',
                'transition-transform hover:scale-105 active:scale-95',
                'outline-none focus-visible:ring-2 focus-visible:ring-foreground',
                'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                className,
            )}
            {...props}
        >
            {children}
        </button>
    ),
);

FloatingActionButton.displayName = 'FloatingActionButton';
